import { useEffect } from 'react'
import { resetConsent } from './CookieConsent'

interface PrivacyPolicyProps {
  isOpen: boolean
  onClose: () => void
}

export default function PrivacyPolicy({ isOpen, onClose }: PrivacyPolicyProps) {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => {
      document.body.style.overflow = ''
    }
  }, [isOpen])

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center">
          <h2 className="text-xl font-bold text-gray-900">Privacy Policy</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 text-2xl leading-none"
            aria-label="Close"
          >
            &times;
          </button>
        </div>

        <div className="px-6 py-4 prose prose-sm max-w-none">
          <p className="text-sm text-gray-500">Last updated: January 2025</p>

          <h3>Introduction</h3>
          <p>
            OffVine Wine ("we," "our," or "us") respects your privacy and is committed to protecting
            your personal data. This privacy policy explains how we collect, use, and safeguard your
            information when you visit offvine.wine (the "Site").
          </p>

          <h3>Information We Collect</h3>
          <p>We collect limited information to improve your experience:</p>
          <ul>
            <li>
              <strong>Analytics Data:</strong> With your consent, we use Google Analytics 4 (GA4) to
              collect anonymized usage data including pages visited, time spent on site, device type,
              browser, and general geographic location (country/region level).
            </li>
            <li>
              <strong>Cookie Preferences:</strong> We store your cookie consent choice locally on your
              device.
            </li>
            <li>
              <strong>Affiliate Tracking:</strong> When you click affiliate links on our site, our
              affiliate partners may collect information to track referrals and attribute sales.
            </li>
          </ul>

          <h3>Cookies We Use</h3>
          <table className="text-sm">
            <thead>
              <tr>
                <th>Cookie</th>
                <th>Purpose</th>
                <th>Duration</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>offvine_cookie_consent</td>
                <td>Stores your cookie preference</td>
                <td>Persistent (localStorage)</td>
              </tr>
              <tr>
                <td>_ga, _ga_*</td>
                <td>Google Analytics (if accepted)</td>
                <td>2 years</td>
              </tr>
              <tr>
                <td>rmStore, ran, rans</td>
                <td>Rakuten Marketing affiliate tracking</td>
                <td>Varies (set by partner sites)</td>
              </tr>
            </tbody>
          </table>
          <p className="text-sm text-gray-600 mt-2">
            Note: Affiliate cookies are set by partner websites (e.g., Wine.com) when you click
            our affiliate links, not directly by OffVine Wine.
          </p>

          <h3>How We Use Your Information</h3>
          <p>We use collected data solely to:</p>
          <ul>
            <li>Understand how visitors use our site</li>
            <li>Improve site performance and content</li>
            <li>Remember your cookie preferences</li>
          </ul>
          <p>We do not sell, rent, or share your personal information with third parties for marketing purposes.</p>

          <h3>Google Analytics</h3>
          <p>
            We use Google Analytics 4 to analyze site traffic. Google Analytics uses cookies to collect
            anonymized data. This data is processed by Google LLC. You can learn more about how Google
            uses this data at{' '}
            <a
              href="https://policies.google.com/privacy"
              target="_blank"
              rel="noopener noreferrer"
              className="text-amber-700 hover:underline"
            >
              Google's Privacy Policy
            </a>.
          </p>
          <p>
            <strong>IP Anonymization:</strong> GA4 does not log or store IP addresses by default.
          </p>

          <h3>Affiliate Links & Advertising</h3>
          <p>
            This site contains affiliate links to third-party wine retailers. When you click these
            links and make a purchase, we may earn a commission at no additional cost to you. This
            helps support the operation of OffVine Wine.
          </p>
          <p>
            Our affiliate partner is{' '}
            <strong>Rakuten Advertising</strong> (Wine.com affiliate program). When you click
            links to Wine.com, Rakuten may set cookies to track your referral. You can learn more
            at{' '}
            <a
              href="https://rakutenadvertising.com/legal-notices/services-privacy-policy/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-amber-700 hover:underline"
            >
              Rakuten's Privacy Policy
            </a>.
          </p>
          <p>
            <strong>Disclosure:</strong> Affiliate relationships do not influence our wine vintage
            ratings or recommendations. Our quality assessments are based on independent research
            of growing conditions and industry reports.
          </p>

          <h3>Your Rights</h3>

          <h4>All Users</h4>
          <ul>
            <li>You can decline analytics cookies when first visiting the site</li>
            <li>You can reset your cookie preferences at any time using the button below</li>
            <li>You can use browser settings to block or delete cookies</li>
          </ul>

          <h4>European Economic Area (EEA) & UK Residents (GDPR)</h4>
          <p>Under GDPR, you have the right to:</p>
          <ul>
            <li>Access your personal data</li>
            <li>Rectify inaccurate data</li>
            <li>Request erasure of your data</li>
            <li>Object to data processing</li>
            <li>Data portability</li>
            <li>Withdraw consent at any time</li>
          </ul>

          <h4>California Residents (CCPA)</h4>
          <p>Under CCPA, you have the right to:</p>
          <ul>
            <li>Know what personal information is collected</li>
            <li>Know if your personal information is sold or disclosed</li>
            <li>Say no to the sale of personal information</li>
            <li>Access your personal information</li>
            <li>Equal service and price, even if you exercise privacy rights</li>
          </ul>
          <p>
            <strong>We do not sell your personal information.</strong>
          </p>

          <h3>Data Retention</h3>
          <p>
            Google Analytics data is retained for 14 months. Your cookie preference is stored
            indefinitely on your device until you clear your browser data or reset preferences.
          </p>

          <h3>Children's Privacy</h3>
          <p>
            This site is intended for users 21 years of age and older. We do not knowingly collect
            information from children under 13.
          </p>

          <h3>Changes to This Policy</h3>
          <p>
            We may update this privacy policy from time to time. We will notify you of any changes by
            posting the new privacy policy on this page and updating the "Last updated" date.
          </p>

          <h3>Contact Us</h3>
          <p>
            If you have questions about this privacy policy or wish to exercise your rights, please
            contact us at:{' '}
            <a href="mailto:privacy@offvine.wine" className="text-amber-700 hover:underline">
              privacy@offvine.wine
            </a>
          </p>

          <h3>Manage Your Preferences</h3>
          <p>
            <button
              onClick={() => {
                if (confirm('This will reset your cookie preferences and reload the page. Continue?')) {
                  resetConsent()
                }
              }}
              className="bg-gray-200 hover:bg-gray-300 px-4 py-2 rounded text-sm"
            >
              Reset Cookie Preferences
            </button>
          </p>
        </div>
      </div>
    </div>
  )
}
